import {useMemo} from 'react';

import type {EmailAutomationStatusesFragment, WidgetFragment} from 'nutshell-graphql-types';

import {EmailSequencesWidget} from '../../../../core-entity/widgets/email-sequences-widget/email-sequences-widget';
import {filterExemptedAutomationStatuses} from '../../../../core-entity/widgets/email-sequences-widget/helpers';
import type {PostMutationCallbacks} from '../../../../core-entity/widgets/email-sequences-widget/types';

import {accountRefetchForEvent} from '../../hooks/use-account-update-refetch';

import {useGetAccountEmailSequenceData} from './graphql/hooks/use-get-account-email-sequence-data';

interface Props {
    accountId: string;
    widget: WidgetFragment;
}

const postMutationCallbacks: PostMutationCallbacks = {
    cancelDrip: () => accountRefetchForEvent('dripSequenceCancel'),
    cancelManual: () => accountRefetchForEvent('manuallyStartedPersonalEmailSequenceCancel'),
    cancelPersonal: () => accountRefetchForEvent('personalEmailSequenceCancel'),
    startPersonal: () => accountRefetchForEvent('personalEmailSequenceStart'),
    sendNow: () => accountRefetchForEvent('personalEmailSequenceSendNow'),
};

export function AccountEmailSequencesWidget(props: Props) {
    const {contacts, queuedEmails, isFirstLoading} = useGetAccountEmailSequenceData(
        props.accountId
    );

    const aggregatedEmailAutomationStatuses = useMemo(() => {
        const statuses: EmailAutomationStatusesFragment[] = [];

        (contacts ?? []).forEach((contact) => {
            statuses.push(...contact.emailAutomationStatuses);
        });

        return statuses;
    }, [contacts]);

    const contactsWithDripProgressions = useMemo(() => {
        if (!contacts) return [];

        return contacts.map((contact) => ({
            id: contact.id,
            name: contact.name,
            dripProgressions: contact.dripProgressions,
        }));
    }, [contacts]);

    return (
        <EmailSequencesWidget
            widget={props.widget}
            contactsWithDripProgressions={contactsWithDripProgressions}
            emailAutomationStatuses={filterExemptedAutomationStatuses(
                aggregatedEmailAutomationStatuses
            )}
            queuedEmails={queuedEmails}
            isFirstLoadingDripSequences={isFirstLoading}
            isFirstLoadingEmailAutomationStatuses={isFirstLoading}
            isFirstLoadingManuallyStartedSequences={isFirstLoading}
            postMutationCallbacks={postMutationCallbacks}
        />
    );
}
